/**
 * OrganizationReports.js
 *
 * This component handles the organization reports functionality in the application.
 * It provides a comprehensive reporting interface with various report types and filtering options.
 *
 * Key features:
 * - Multiple report types (PRIME, Indiana, Unity, etc.)
 * - Date range selection
 * - Organization/Location/Program filtering
 * - Custom column selection
 * - Export functionality (CSV/Excel)
 * - Data visualization and filtering
 */

import React, { useContext, useState, useRef, useEffect } from "react"
import SidebarDashboardSupervisor from "../Homepage/sidebars/SidebarDashboardSupervisor"

import _ from "lodash"
import { useQuery, useMutation, useLazyQuery } from "@apollo/client"

// Prime React Components - UI library components for the reporting interface
import { FilterMatchMode, FilterOperator } from "primereact/api"
import { Tooltip } from "primereact/tooltip"
import { DataTable } from "primereact/datatable"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { Panel } from "primereact/panel"
import { MultiSelect } from "primereact/multiselect"
import { Ripple } from "primereact/ripple"
import { Messages } from "primereact/messages"
import { Dropdown } from "primereact/dropdown"

// Date handling components and utilities
import {
  DateRangePicker,
  defaultStaticRanges,
  defaultInputRanges,
} from "react-date-range"
import {
  addDays,
  subDays,
  endOfDay,
  startOfYear,
  endOfYear,
  addYears,
  isSameDay,
  format,
  isWeekend,
} from "date-fns"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"

// Context for user authentication and session management
import { UserContext } from "../../context/userContext"

// GraphQL Queries - Import all report-related queries
import {
  ENTERPRISE,
  ORGANIZATION,
  LOCATION,
  PROGRAM,
  STATUS,
  STAFF,
  TEMPLATE,
  BLOCK,
  COLUMN,
  CUSTOM_COLUMN,
  ISR,
  ISR_GROUP_SESSION,
  ORGANIZATION_REPORTS,
  GPRA_COLUMN,
  GPRA_REPORTS,
  MICROSERVICES_REPORT_OPTIONS,
  TELERECOVERY_SESSIONS_REPORT,
  REFERRALS_REPORT,
  INDIANA_OS_REPORT,
  INDIANA_ACTIVITY_SESSION_REPORT,
  INDIANA_DEMOGRAPHICS_REPORT,
  INDIANA_DIRECT_SERVICE_REPORT,
  INDIANA_ENGAGEMENT_REPORT,
  INDIANA_GPRA_REPORT,
  INDIANA_LIST_REFERRALS_REPORT,
  INDIANA_LYFT_DROPOFF_REPORT,
  INDIANA_LYFT_REPORT,
  INDIANA_REFERRALS_REPORT,
  INDIANA_SUPERVISION_REPORT,
  INDIANA_CONTACT_LOG_REPORT,
  PRIME_CHRACTERISTICS_REPORT,
  PRIME_DEMOGRAPHICS_REPORT,
  PRIME_INTAKES_REPORT,
  PRIME_CHECK_INS_REPORT,
  PRIME_GPRA_REPORT,
  KIOSK_ACTIVITY_REPORT,
  KIOSK_CONSENT_REPORT,
  BARC10_MONTHLY_REPORT,
  BARC10_INDIVIDUAL_REPORT,
  UNITY_RSS_REPORT,
  UNITY_EMPLOYMENT_REPORT,
  UNITY_HR_REPORT,
  UNITY_PPR_REPORT,
  SAGES_RSS_REPORT,
  PARTICIPANT_COLUMN,
  PARTICIPANT_CONTACT_REPORT,
  PARTICIPANT_DEMOGRAPHICS_REPORT,
  PARTICIPANT_ENGAGED_REPORT,
  PARTICIPANT_CHARACTERISTICS_REPORT,
  NH_MEDICAID_BILLING_REPORT,
} from "./queries"
import styles from "../../styles/Reporting.module.css"
import { ms } from "date-fns/locale"

// Constants for field names to avoid magic strings
const NUMERIC_FIELDS = [
  "activity_distributed",
  "activity_attendance_hr",
  "activity_attendance",
  "session_length",
]

const DOCUMENT_PARTICIPANT_FILE_KEY = "document_participant_file"

/**
 * Utility function to extract filename from a file path
 * @param {string} pathString - The file path string
 * @returns {string|null} The filename or null if invalid input
 */
function getFilename(pathString) {
  if (typeof pathString !== "string") return null
  const parts = pathString.split("/")
  return parts[parts.length - 1] || null
}

/**
 * Main OrganizationReports component
 * Handles the organization reports interface and functionality
 */
export default function OrganizationReports() {
  // User context for authentication and session management
  const userCtx = useContext(UserContext)
  const user = userCtx.session.user

  // State management for filters and selections
  const [organization, setOrganization] = useState(null)
  const [location, setLocation] = useState(null)
  const [program, setProgram] = useState(null)
  const [status, setStatus] = useState(null)
  const [staff, setStaff] = useState(null)
  const [facilitating_staff, setFacilitatingStaff] = useState(null)
  const [type, setType] = useState(null)
  const [subtype, setSubtype] = useState(null)
  const [template, setTemplate] = useState(null)
  const [block, setBlock] = useState(null)
  const [column, setColumn] = useState(null)
  const [custom_report_column, setCustomReportColumn] = useState(null)
  const [microservices_report_column, setMicroServicesReportColumn] =
    useState(null)
  const [participant_report_column, setParticipantReportColumn] = useState(null)
  const dt = useRef(null)
  const message = useRef(null)
  const [loading, setLoading] = useState(false)
  const [clear, setClear] = useState(false)
  const [dtfilter, setDtfilter] = useState(null)
  const [dtreorder, setDtreorder] = useState(null)
  const onHover = () => {
    message.current.show({
      life: 20000,
      severity: "info",
      detail:
        "Various filters can be set to modify the data in report selections. Programs, locations, status, and staffs are specific to the participants appearing in reports with the exception of activity reports which are organization-specific.",
    })
  }
  const onLeave = () => {
    message.current.clear()
  }

  const [date_range, setDateRange] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      key: "selection",
    },
  ])

  const { data: enterprise_data, loading: enterprise_loading } = useQuery(
    ENTERPRISE,
    {
      variables: {
        role_id: user.staff_role,
        enterprise_id: user.enterprise_id,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(user.enterprise_id),
    },
  )

  const { data: organization_data, loading: organization_loading } = useQuery(
    ORGANIZATION,
    {
      variables: {
        role_id: user.staff_role,
        organization_id: user?.organization_id || "",
        enterprise_id: user.enterprise_id,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(user.enterprise_id),
    },
  )

  const organization_transform = (() => {
    if (organization) {
      return organization.map((x) => "'" + x + "'").join(",")
    } else {
      return organization
    }
  })()

  const { data: location_data, loading: location_loading } = useQuery(
    LOCATION,
    {
      variables: {
        organization_id: organization_transform,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(organization_transform),
    },
  )

  const { data: program_data, loading: program_loading } = useQuery(PROGRAM, {
    variables: {
      organization_id: organization_transform,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(organization_transform),
  })

  const { data: status_data, loading: status_loading } = useQuery(STATUS, {
    variables: {
      organization_id: organization_transform,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(organization_transform),
  })

  const { data: staff_data, loading: staff_loading } = useQuery(STAFF, {
    variables: {
      organization_id: organization_transform,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(organization_transform),
  })

  const { data: template_data, loading: template_loading } = useQuery(
    TEMPLATE,
    {
      variables: {
        organization_id: organization_transform,
        report_type: subtype,
      },
      fetchPolicy: "network-only",
      skip:
        _.isEmpty(organization_transform) ||
        _.isEmpty(subtype) ||
        type !== "Organization Reporting",
    },
  )
  const { data: msreportoptionsdata, error: msreportoptionserror } = useQuery(
    MICROSERVICES_REPORT_OPTIONS,
    {
      variables: {
        enterprise_id: user.enterprise_id,
      },
      fetchPolicy: "network-only",
      onError: (error) => {
        console.log("Fetching microservices report options: ", error)
      },
    },
  )

  const organization_transform_name = (() => {
    if (organization) {
      const output = organization.map((x) => {
        const name = organization_data.ReportOrganization.filter(
          (item) => item.organization_id === x,
        ).map((col) => {
          return { name: col.organization_name, id: x }
        })
        return name
      })
      return output
    } else {
      return organization
    }
  })()

  const organization_options = (() => {
    if (enterprise_data && organization_data) {
      const organizations = enterprise_data.ReportEnterprise.map(
        (enterprise) => {
          const items = organization_data.ReportOrganization.filter(
            (item) => item.enterprise_id === enterprise.enterprise_id,
          ).map((col) => {
            return { value: col.organization_id, label: col.organization_name }
          })
          return { label: enterprise.enterprise_name, items: items }
        },
      )
      return organizations
    } else {
      return null
    }
  })()

  const location_options = (() => {
    if (organization && location_data) {
      const locations = organization_transform_name.map(([organization]) => {
        const items = location_data.ReportLocation.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return { value: col.location_id, label: col.location_name }
        })
        return { label: organization.name, items: items }
      })
      return locations
    } else {
      return null
    }
  })()

  const program_options = (() => {
    if (organization && program_data) {
      const programs = organization_transform_name.map(([organization]) => {
        const items = program_data.ReportProgram.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return { value: col.program_identity_id, label: col.program_id_name }
        })
        return { label: organization.name, items: items }
      })
      return programs
    } else {
      return null
    }
  })()

  const status_options = (() => {
    if (organization && status_data) {
      const status = organization_transform_name.map(([organization]) => {
        const items = status_data.ReportStatus.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return {
            value: col.participant_membership_status,
            label: col.participant_membership_status,
          }
        })
        return { label: organization.name, items: items }
      })
      return status
    } else {
      return null
    }
  })()

  const staff_options = (() => {
    if (organization && staff_data) {
      const staff = organization_transform_name.map(([organization]) => {
        const items = staff_data.ReportStaff.filter(
          (item) => item.organization_id === organization.id,
        ).map((col) => {
          return { value: col.staff, label: col.staff }
        })
        return { label: organization.name, items: items }
      })
      return staff
    } else {
      return null
    }
  })()

  const report_type = [
    { value: "Organization Reporting", label: "Organization Reporting" },
    { value: "Microservices Reporting", label: "Microservices Reporting" },
    { value: "Custom Reporting", label: "Custom Reporting" },
    { value: "Participant Reporting", label: "Participant Reporting" },
  ]

  const report_sub_type = (() => {
    if (type === "Organization Reporting") {
      return [
        {
          label: "Organization Reporting",
          items: [
            { value: "Activity", label: "Activity" },
            { value: "Data Form", label: "Data Form" },
            { value: "Session", label: "Session" },
          ],
        },
      ]
    } else if (type === "Custom Reporting") {
      // Code to include Indiana custom reports if one or more organizations from Indiana is selected by staff
      const { ReportOrganization: organizationList } = organization_data

      const indianaOrganizations = _.reduce(
        organizationList,
        (acc, org) => {
          const indianaEntId = "68412bfd-57f4-40ad-af62-88fc003360ed"
          if (org.enterprise_id === indianaEntId) {
            return [...acc, org.organization_id]
          }
          return acc
        },
        [],
      )

      function hasIndianaOrganizationSelected() {
        for (const orgId of organization) {
          if (indianaOrganizations.includes(orgId)) {
            return true
          }
        }
        return false
      }

      if (user.staff_role === 1) {
        let defaultList = [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "Activity Session",
                label: "Indiana SSA Activity Session Report",
              },
              {
                value: "Demographics",
                label: "Indiana SSA Demographics Report",
              },
              {
                value: "Direct Service Hours",
                label: "Indiana SSA Direct Service Hours Report",
              },
              { value: "Engagements", label: "Indiana SSA Engagements Report" },
              { value: "GPRA", label: "Indiana SSA GPRA Report" },
              {
                value: "Individual List of Incoming Referrals",
                label:
                  "Indiana SSA Individual List of Incoming Referrals Report",
              },
              {
                value: "Lyft Drop-off Location",
                label: "Indiana SSA Lyft Drop-off Location Report",
              },
              { value: "Lyft", label: "Indiana SSA Lyft Report" },
              { value: "Supervision", label: "Indiana SSA Supervision Report" },
              {
                value: "DOC Incoming Referrals",
                label: "Indiana SSA DOC Incoming Referrals Report",
              },
              {
                value: "New Hampshire Medicaid Billing Report",
                label: "New Hampshire Medicaid Billing Report",
              },
              {
                value: "PRIME Participant Characteristics Report",
                label: "PRIME Participant Characteristics Report",
              },
              {
                value: "PRIME Participant Demographics Report",
                label: "PRIME Participant Demographics Report",
              },

              {
                value: "PRIME Intakes Report",
                label: "PRIME Intakes Report",
              },
              {
                value: "PRIME Check-ins Report",
                label: "PRIME Check-ins Report",
              },
              {
                value: "PRIME GPRA Report",
                label: "PRIME GPRA Report",
              },
              {
                value: "Unity Recovery RSS Report",
                label: "Unity Recovery RSS Report",
              },
              {
                value: "Unity Recovery Employment Report",
                label: "Unity Recovery Employment Report",
              },
              {
                value: "Unity Recovery Harm Reduction Report",
                label: "Unity Recovery Harm Reduction Report",
              },
              {
                value: "Unity Recovery PPR Report",
                label: "Unity Recovery PPR Report",
              },
              {
                value: "Sages Army RSS Report",
                label: "Sages Army RSS Report",
              },
            ],
          },
        ]

        // TODO: This is a hack to add the Opioid Stimulant Specific and Contact Log custom reports
        // report to the list of reports
        // Please refactor....
        if (hasIndianaOrganizationSelected()) {
          defaultList[0].items.push({
            value: "Opioid Stimulant Specific",
            label: "Opioid Stimulant Specific",
          })
          defaultList[0].items.push({
            value: "Contact Log",
            label: "Contact Log",
          })
        }
        const sortedList = _.sortBy(defaultList[0].items, ["label"])
        defaultList[0].items = sortedList
        return defaultList
      } else if (
        user.enterprise_id === "68412bfd-57f4-40ad-af62-88fc003360ed"
      ) {
        // Indiana SSA (Enterprise) custom reports options
        // Opioiod Stimulant Specific and Contact Log reports are only available to Indiana SSA staff
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "Activity Session",
                label: "Indiana SSA Activity Session Report",
              },
              { value: "Contact Log", label: "Contact Log" },
              {
                value: "Demographics",
                label: "Indiana SSA Demographics Report",
              },
              {
                value: "Direct Service Hours",
                label: "Indiana SSA Direct Service Hours Report",
              },
              { value: "Engagements", label: "Indiana SSA Engagements Report" },
              { value: "GPRA", label: "Indiana SSA GPRA Report" },
              {
                value: "Individual List of Incoming Referrals",
                label:
                  "Indiana SSA Individual List of Incoming Referrals Report",
              },
              {
                value: "Lyft Drop-off Location",
                label: "Indiana SSA Lyft Drop-off Location Report",
              },
              { value: "Lyft", label: "Indiana SSA Lyft Report" },
              { value: "Supervision", label: "Indiana SSA Supervision Report" },
              {
                value: "DOC Incoming Referrals",
                label: "Indiana SSA DOC Incoming Referrals Report",
              },
            ],
          },
        ]
      } else if (
        user.enterprise_id === "b48067e0-73a9-459f-ad0a-8b5856403b9d" &&
        (user.staff_role === 2 || user.staff_role === 3) // User Role update ERR-1016. Adding staff_role = 3
      ) {
        // PRIME Oregon enterprise custom reports options
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "PRIME Participant Characteristics Report",
                label: "PRIME Participant Characteristics Report",
              },
              {
                value: "PRIME Participant Demographics Report",
                label: "PRIME Participant Demographics Report",
              },

              {
                value: "PRIME Intakes Report",
                label: "PRIME Intakes Report",
              },
              {
                value: "PRIME Check-ins Report",
                label: "PRIME Check-ins Report",
              },
              {
                value: "PRIME GPRA Report",
                label: "PRIME GPRA Report",
              },
            ],
          },
        ]
      } else if (
        user.enterprise_id === "78654629-b355-4ed3-abb8-6c3db1af127f"
      ) {
        // Unity Recovery (Enterprise) custom reports options
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "Unity Recovery RSS Report",
                label: "Unity Recovery RSS Report",
              },
              {
                value: "Unity Recovery Employment Report",
                label: "Unity Recovery Employment Report",
              },
              {
                value: "Unity Recovery Harm Reduction Report",
                label: "Unity Recovery Harm Reduction Report",
              },
              {
                value: "Unity Recovery PPR Report",
                label: "Unity Recovery PPR Report",
              },
            ],
          },
        ]
      } else if (
        user.enterprise_id === "7f4d2ff2-f5c5-49fa-a3f8-2dbb91477619"
      ) {
        // Sages Army (Enterprise) custom reports options
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "Sages Army RSS Report",
                label: "Sages Army RSS Report",
              },
            ],
          },
        ]
      } else if (
        user.enterprise_id === "be6981d0-3202-44f1-aec9-d62d712642a7"
      ) {
        // New Hampshire (Enterprise) custom reports options
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
              {
                value: "New Hampshire Medicaid Billing Report",
                label: "New Hampshire Medicaid Billing Report",
              },
            ],
          },
        ]
      } else {
        return [
          {
            label: "Custom Reporting",
            items: [
              {
                value: "ISR Individual Sessions",
                label: "ISR Individual Sessions",
              },
              { value: "ISR Group Sessions", label: "ISR Group Sessions" },
            ],
          },
        ]
      }
    } else if (type === "Microservices Reporting") {
      const msReportOptionsItems =
        msreportoptionsdata?.ReportMicroservicesReportOptions.map((option) => {
          return { value: option.option_name, label: option.option_name }
        })

      if (user.staff_role === 1) {
        return [
          {
            label: "Microservices Reporting",
            items: [
              { value: "GPRA Report", label: "GPRA Report" },
              {
                value: "Kiosk Check-in Activity Report",
                label: "Kiosk Check-in Activity Report",
              },
              {
                value: "Referrals Report",
                label: "Referrals Report",
              },
              {
                value: "Assessments BARC-10 Monthly Report",
                label: "Assessments BARC-10 Monthly Report",
              },
              {
                value: "Assessments BARC-10 Report",
                label: "Assessments BARC-10 Report",
              },
              {
                value: "Telerecovery Sessions Report",
                label: "Telerecovery Sessions Report",
              },
            ],
          },
        ]
      } else {
        return [
          {
            label: "Microservices Reporting",
            items: msReportOptionsItems,
          },
        ]
      }
    } else if (type === "Participant Reporting") {
      return [
        {
          label: "Participant Reporting",
          items: [
            {
              value: "Participant Contact Information",
              label: "Participant Contact Information",
            },
            {
              value: "Participant Demographics",
              label: "Participant Demographics",
            },
            {
              value: "Participant Engaged Services",
              label: "Participant Engaged Services",
            },
            {
              value: "Participant Recovery Characteristics",
              label: "Participant Recovery Characteristics",
            },
          ],
        },
      ]
    }
  })()

  const template_options = (() => {
    if (type === "Organization Reporting" && template_data) {
      const templates = template_data.ReportTemplate.map((template) => {
        return {
          value: template.template_title,
          label: template.template_title,
        }
      })
      return [{ label: subtype, items: templates }]
    } else {
      return null
    }
  })()

  const template_transform = (() => {
    if (template) {
      return "'" + template + "'"
    } else {
      return template
    }
  })()

  const { data: block_data, loading: block_loading } = useQuery(BLOCK, {
    variables: {
      organization_id: organization_transform,
      template_title: template_transform,
      report_type: subtype,
    },
    fetchPolicy: "network-only",
    skip:
      _.isEmpty(organization_transform) ||
      _.isEmpty(template_transform) ||
      _.isEmpty(subtype),
  })

  const block_options = (() => {
    if (type === "Organization Reporting" && block_data) {
      const blocks = block_data.ReportBlock.map((col) => {
        return { value: col.block_name, label: col.block_name }
      })
      return [{ label: template, items: blocks }]
    } else {
      return null
    }
  })()

  const block_transform = (() => {
    if (block) {
      return block.map((x) => "'" + x + "'").join(",")
    } else {
      return block
    }
  })()

  const { data: column_data, loading: column_loading } = useQuery(COLUMN, {
    variables: {
      organization_id: organization_transform,
      template_title: template_transform,
      block_name: block_transform,
      report_type: subtype,
    },
    fetchPolicy: "network-only",
    skip:
      _.isEmpty(organization_transform) ||
      _.isEmpty(template_transform) ||
      _.isEmpty(block_transform) ||
      _.isEmpty(subtype),
  })

  const column_options = (() => {
    if (block && column_data) {
      const columns = block.map((blocks) => {
        const items = column_data.ReportColumn.filter(
          (item) =>
            item.template_title === template && item.block_name === blocks,
        ).map((col) => {
          return { value: col.field_name, label: col.field_name }
        })
        return { label: blocks, items: items }
      })
      return columns
    } else {
      return null
    }
  })()

  const { data: custom_column_data, loading: custom_column_loading } = useQuery(
    CUSTOM_COLUMN,
    {
      variables: {
        table_name: subtype,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(subtype) || type !== "Custom Reporting",
    },
  )

  const custom_column_options = (() => {
    if (type === "Custom Reporting" && custom_column_data) {
      const custom_columns = custom_column_data.ReportCustomColumn.map(
        (col) => {
          return { value: col.column_name, label: col.column_name }
        },
      )
      return [{ label: subtype, items: custom_columns }]
    } else {
      return null
    }
  })()

  const {
    data: microservices_column_data,
    loading: microservices_column_loading,
  } = useQuery(GPRA_COLUMN, {
    variables: {
      table_name: subtype,
    },
    fetchPolicy: "network-only",
    skip: _.isEmpty(subtype) || type !== "Microservices Reporting",
  })

  const microservices_column_options = (() => {
    if (type === "Microservices Reporting" && microservices_column_data) {
      const microservices_columns =
        microservices_column_data.ReportMicroservicesColumns.map((col) => {
          return { value: col.column_name, label: col.column_name }
        })
      return [{ label: subtype, items: microservices_columns }]
    } else {
      return null
    }
  })()

  const { data: participant_column_data, loading: participant_column_loading } =
    useQuery(PARTICIPANT_COLUMN, {
      variables: {
        table_name: subtype,
      },
      fetchPolicy: "network-only",
      skip: _.isEmpty(subtype) || type !== "Participant Reporting",
    })

  const validParticipantSubtypes = [
    "Participant Recovery Characteristics",
    "Participant Engaged Services",
    "Participant Demographics",
    "Participant Contact Information",
  ]

  const participantColumnRenameMap = {
    program_identity_names: "participant_assigned_programs",
    location_names: "participant_assigned_locations",
  }

  const shouldRenameParticipantColumns =
    type === "Participant Reporting" &&
    validParticipantSubtypes.includes(subtype)

  const normalizeParticipantColumnName = (columnName) => {
    if (!shouldRenameParticipantColumns) {
      return columnName
    }

    return participantColumnRenameMap[columnName] || columnName
  }

  const getParticipantFieldValue = (row, columnName) => {
    if (columnName === "participant_assigned_programs") {
      return row[columnName] ?? row.program_identity_names
    }

    if (columnName === "participant_assigned_locations") {
      return row[columnName] ?? row.location_names
    }

    return row[columnName]
  }

  const normalizedParticipantReportColumns = participant_report_column
    ? participant_report_column.map((col) =>
        normalizeParticipantColumnName(col),
      )
    : null
  const selectedParticipantReportColumns =
    normalizedParticipantReportColumns || []

  const participant_column_options = (() => {
    if (type === "Participant Reporting" && participant_column_data) {
      const participant_columns =
        participant_column_data.ReportParticipantColumns.map((col) => {
          const columnName = normalizeParticipantColumnName(col.column_name)
          return { value: columnName, label: columnName }
        })

      return [{ label: subtype, items: participant_columns }]
    }

    return null
  })()

  // Generate reports and download reports section
  const [fetchisr, { data: isrdata, error: isrerror }] = useLazyQuery(ISR)
  const [fetchisrgroup, { data: isrgroupdata, error: isrgrouperror }] =
    useLazyQuery(ISR_GROUP_SESSION)
  const [fetchgpra, { data: gpradata, error: gpraerror }] =
    useLazyQuery(GPRA_REPORTS)

  const [fetchos, { data: osdata, error: oserror }] =
    useLazyQuery(INDIANA_OS_REPORT)
  const [fetchac, { data: acdata, error: acerror }] = useLazyQuery(
    INDIANA_ACTIVITY_SESSION_REPORT,
  )
  const [fetchdm, { data: dmdata, error: dmerror }] = useLazyQuery(
    INDIANA_DEMOGRAPHICS_REPORT,
  )
  const [fetchds, { data: dsdata, error: dserror }] = useLazyQuery(
    INDIANA_DIRECT_SERVICE_REPORT,
  )
  const [fetcheg, { data: egdata, error: egerror }] = useLazyQuery(
    INDIANA_ENGAGEMENT_REPORT,
  )
  const [fetchgp, { data: gpdata, error: gperror }] =
    useLazyQuery(INDIANA_GPRA_REPORT)
  const [fetchlr, { data: lrdata, error: lrerror }] = useLazyQuery(
    INDIANA_LIST_REFERRALS_REPORT,
  )
  const [fetchld, { data: lddata, error: lderror }] = useLazyQuery(
    INDIANA_LYFT_DROPOFF_REPORT,
  )
  const [fetchly, { data: lydata, error: lyerror }] =
    useLazyQuery(INDIANA_LYFT_REPORT)
  const [fetchre, { data: redata, error: reerror }] = useLazyQuery(
    INDIANA_REFERRALS_REPORT,
  )
  const [fetchsp, { data: spdata, error: sperror }] = useLazyQuery(
    INDIANA_SUPERVISION_REPORT,
  )
  const [fetchcl, { data: cldata, error: clerror }] = useLazyQuery(
    INDIANA_CONTACT_LOG_REPORT,
  )
  const [
    fetchprimechracteristics,
    { data: primecharacteristicsdata, error: primecharacteristicserror },
  ] = useLazyQuery(PRIME_CHRACTERISTICS_REPORT)
  const [
    fetchprimedemographics,
    { data: primedemographicsdata, error: primedemographicserror },
  ] = useLazyQuery(PRIME_DEMOGRAPHICS_REPORT)
  //New PRIME reporte
  const [
    fetchprimeintakes,
    { data: primeintakesdata, error: primeintakeserror },
  ] = useLazyQuery(PRIME_INTAKES_REPORT)
  const [
    fetchprimecheckins,
    { data: primecheckinsdata, error: primecheckinserror },
  ] = useLazyQuery(PRIME_CHECK_INS_REPORT)
  const [fetchprimegpra, { data: primegpradata, error: primegpraerror }] =
    useLazyQuery(PRIME_GPRA_REPORT)
  //New PRIME ends
  const [
    fetchkioskactivity,
    { data: kioskactivitydata, error: kioskactivityerror },
  ] = useLazyQuery(KIOSK_ACTIVITY_REPORT)
  const [
    fetchkioskconsent,
    { data: kioskconsentdata, error: kioskconsenterror },
  ] = useLazyQuery(KIOSK_CONSENT_REPORT)
  const [fetchreferrals, { data: referralsdata, error: referralserror }] =
    useLazyQuery(REFERRALS_REPORT)
  const [
    fetchbarc10monthly,
    { data: barc10monthlydata, error: barc10monthlyerror },
  ] = useLazyQuery(BARC10_MONTHLY_REPORT)
  const [
    fetchbarc10individual,
    { data: barc10individualdata, error: barc10individualerror },
  ] = useLazyQuery(BARC10_INDIVIDUAL_REPORT)
  const [
    fetchtelerecoverysessions,
    { data: telerecoverysessionsdata, error: telerecoverysessionserror },
  ] = useLazyQuery(TELERECOVERY_SESSIONS_REPORT)
  const [fetchunityrss, { data: unityrssdata, error: unityrsserror }] =
    useLazyQuery(UNITY_RSS_REPORT)
  const [
    fetchunityemployment,
    { data: unityemploymentdata, error: unityemploymenterror },
  ] = useLazyQuery(UNITY_EMPLOYMENT_REPORT)
  const [fetchunityhr, { data: unityhrdata, error: unityhrerror }] =
    useLazyQuery(UNITY_HR_REPORT)
  const [fetchunityppr, { data: unitypprdata, error: unitypprerror }] =
    useLazyQuery(UNITY_PPR_REPORT)
  const [fetchsagesrss, { data: sagesrssdata, error: sagesrsserror }] =
    useLazyQuery(SAGES_RSS_REPORT)
  const [
    fetchparticipantcontact,
    { data: participantcontactdata, error: participantcontacterror },
  ] = useLazyQuery(PARTICIPANT_CONTACT_REPORT)
  const [
    fetchparticipantdemographics,
    { data: participantdemographicsdata, error: participantcdemographicserror },
  ] = useLazyQuery(PARTICIPANT_DEMOGRAPHICS_REPORT)
  const [
    fetchparticipantengaged,
    { data: participantengageddata, error: participantengagederror },
  ] = useLazyQuery(PARTICIPANT_ENGAGED_REPORT)
  const [
    fetchparticipantcharacteristics,
    {
      data: participantcharacteristicsdata,
      error: participantcharacteristicserror,
    },
  ] = useLazyQuery(PARTICIPANT_CHARACTERISTICS_REPORT)
  const [
    fetchnhmedicaidbilling,
    { data: nhmedicaidbillingdata, error: nhmedicaidbillingerror },
  ] = useLazyQuery(NH_MEDICAID_BILLING_REPORT)

  const organization_transform_name_join = (() => {
    if (organization_transform_name) {
      const output = organization_transform_name
        .map(([x]) => "'" + x.name + "'")
        .join(",")
      return output
    } else {
      return organization_transform_name
    }
  })()

  const location_transform = (() => {
    if (location_data && location) {
      const output = location.map((x) => {
        const name = location_data.ReportLocation.filter(
          (item) => item.location_id === x,
        ).map((col) => {
          return col.location_name
            .replace("(", "[(]")
            .replace(")", "[)]")
            .replace('"', '\\"')
        })
        return name
      })
      return output
    } else {
      return location
    }
  })()

  const location_transform_name = (() => {
    if (location_transform && location_transform.length !== 0) {
      const output = location_transform.map(([x]) => x).join("|")
      return `"` + output + `"`
    } else {
      return " "
    }
  })()

  const program_transform = (() => {
    if (program_data && program) {
      const output = program.map((x) => {
        const name = program_data.ReportProgram.filter(
          (item) => item.program_identity_id === x,
        ).map((col) => {
          return col.program_id_name
            .replace("(", "[(]")
            .replace(")", "[)]")
            .replace('"', '\\"')
        })
        return name
      })
      return output
    } else {
      return program
    }
  })()

  const program_transform_name = (() => {
    if (program_transform && program_transform.length !== 0) {
      const output = program_transform.map(([x]) => x).join("|")
      return `"` + output + `"`
    } else {
      return " "
    }
  })()

  const program_transform_name_gpra = (() => {
    if (program && program.length !== 0) {
      const output = program_transform.map(([x]) => `"` + x + `"`).join(",")
      return output
    } else {
      return " "
    }
  })()

  const status_transform = (() => {
    if (status && status.length !== 0) {
      return status.map((x) => `"` + x + `"`).join(",")
    } else {
      return " "
    }
  })()

  const staff_transform = (() => {
    if (staff && staff.length !== 0) {
      return staff.map((x) => `"` + x + `"`).join(",")
    } else {
      return " "
    }
  })()

  const facilitating_staff_transform = (() => {
    if (facilitating_staff && facilitating_staff.length !== 0) {
      return facilitating_staff.map((x) => `"` + x + `"`).join(",")
    } else {
      return " "
    }
  })()

  const column_transform = (() => {
    if (column) {
      return column.map((x) => "'" + x + "'").join(",")
    } else {
      return column
    }
  })()

  /**
   * GraphQL query hook for fetching organization reports data
   * Uses lazy query to control when the data is fetched
   */
  const [
    fetchorganizationreports,
    { data: organization_reports_data, error: organization_reports_error },
  ] = useLazyQuery(ORGANIZATION_REPORTS)

  /**
   * Transforms the raw organization reports data into a structured format
   * - Maps the data to a consistent object structure
   * - Handles special numeric fields (activity_distributed, activity_attendance_hr, etc.)
   * - Parses and processes the fields_json data
   * @returns {Array|null} Transformed data array or null if no data available
   */

  const organization_reports_data_transform = (() => {
    if (organization_reports_data) {
      const output = organization_reports_data.ReportOrganizationReports.map(
        (row) => {
          // Base object with standard fields
          let rowObject = {
            organization_name: row.organization_name,
            location_names: row.location_names,
            program_identity_names: row.program_identity_names,
            participant_membership_status: row.participant_membership_status,
            assigned_staff_id: row.assigned_staff_id,
            participant_membership_id: row.participant_membership_id,
            participant_alias: row.participant_alias,
            participant_name_first: row.participant_name_first,
            participant_name_last: row.participant_name_last,
            participant_email_account: row.participant_email_account,
            facilitating_staff_alias_id: row.facilitating_staff_alias_id,
            facilitating_staff_name_first: row.facilitating_staff_name_first,
            facilitating_staff_name_last: row.facilitating_staff_name_last,
          }

          // Process additional fields from fields_json
          const fieldsJsonObj = JSON.parse(row.fields_json || "{}")
          for (const [key, value] of Object.entries(fieldsJsonObj)) {
            // Convert specific fields to numbers if they contain numeric values
            if (NUMERIC_FIELDS.includes(key) && value !== null) {
              rowObject[key] = Number(value)
            } else if (
              // ERR-2004: Document Participant File
              key === DOCUMENT_PARTICIPANT_FILE_KEY &&
              value !== null
            ) {
              rowObject[key] = getFilename(value) || value
            } else {
              rowObject[key] = value
            }
          }

          return rowObject
        },
      )

      return output
    } else {
      return null
    }
  })()

  /**
   * Exports the current data table view to CSV format
   * Handles large datasets with progress indicator
   * @param {boolean} selectionOnly - If true, only exports selected rows
   */
  const exportCSV = (selectionOnly) => {
    const value = dtfilter ? dtfilter : dt.current.props.value
    const totalRows = value.length

    // For large datasets, show warning and use optimized export
    if (totalRows > 10000) {
      const shouldContinue = window.confirm(
        `You're trying to export ${totalRows.toLocaleString()} rows to CSV. ` +
          `This may take some time. Continue?`,
      )

      if (!shouldContinue) {
        return
      }

      // Show progress for large CSV exports
      const progressDiv = document.createElement("div")
      progressDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border: 2px solid #28a745;
        border-radius: 10px;
        padding: 20px;
        z-index: 9999;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        min-width: 350px;
      `
      progressDiv.innerHTML = `
        <div style="text-align: center;">
          <h4>Processing CSV Export...</h4>
          <div id="csv-progress-text">Processing rows 0 of ${totalRows.toLocaleString()}</div>
          <div style="width: 300px; height: 20px; background: #f0f0f0; border-radius: 10px; margin: 10px 0;">
            <div id="csv-progress-bar" style="width: 0%; height: 100%; background: #28a745; border-radius: 10px; transition: width 0.3s;"></div>
          </div>
          <div id="csv-progress-percentage" style="font-size: 12px; color: #666;">0%</div>
        </div>
      `
      document.body.appendChild(progressDiv)

      // Use setTimeout to allow UI to update
      setTimeout(() => {
        try {
          dt.current.exportCSV({ selectionOnly }, "exportName")
          document.body.removeChild(progressDiv)
        } catch (error) {
          console.error("Error exporting CSV:", error)
          document.body.removeChild(progressDiv)
          alert("Error exporting CSV. Please try again.")
        }
      }, 100)
    } else {
      // For smaller datasets, use the original approach
      dt.current.exportCSV({ selectionOnly }, "exportName")
    }
  }

  /**
   * Saves the provided buffer as an Excel file
   * @param {Buffer} buffer - The data buffer to save
   * @param {string} fileName - The name of the file to save
   */
  const saveAsExcelFile = (buffer, fileName) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        let EXCEL_EXTENSION = ".xlsx"
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        })

        module.default.saveAs(data, fileName + EXCEL_EXTENSION)
      }
    })
  }

  /**
   * Exports the current data table view to Excel format
   * Handles different report types and column ordering with chunked processing for large datasets
   */
  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const value = dtfilter ? dtfilter : dt.current.props.value

      // For large datasets, use chunked processing
      // Adjust chunk size based on data complexity and system capabilities
      const getChunkSize = () => {
        const totalRows = value.length
        const totalColumns =
          type === "Custom Reporting"
            ? custom_report_column?.length || 0
            : type === "Microservices Reporting"
              ? microservices_report_column?.length || 0
              : type === "Participant Reporting"
                ? participant_report_column?.length || 0
                : column?.length || 0

        // Chrome-specific optimizations: Use smaller chunks to prevent memory issues
        const isChrome =
          /Chrome/.test(navigator.userAgent) &&
          /Google Inc/.test(navigator.vendor)

        if (isChrome) {
          // Chrome tends to have memory issues with large chunks
          if (totalRows > 50000 && totalColumns > 50) {
            return 250
          } else if (totalRows > 25000 && totalColumns > 30) {
            return 500
          } else {
            return 750
          }
        } else {
          // For other browsers, use original chunk sizes
          if (totalRows > 50000 && totalColumns > 50) {
            return 500
          } else if (totalRows > 25000 && totalColumns > 30) {
            return 750
          } else {
            return 1000
          }
        }
      }

      const CHUNK_SIZE = getChunkSize()
      const totalRows = value.length

      // For extremely large datasets (>50k rows), suggest server-side export
      if (totalRows > 50000) {
        const shouldUseServerSide = window.confirm(
          `You're trying to export ${totalRows.toLocaleString()} rows. This may cause browser performance issues. ` +
            `Would you like to use server-side export instead? (Recommended for datasets >50k rows)`,
        )

        if (shouldUseServerSide) {
          // TODO: Implement server-side export endpoint
          alert(
            "Server-side export feature is not yet implemented. Please contact your administrator.",
          )
          return
        }
      }

      if (totalRows > CHUNK_SIZE) {
        // Show progress indicator
        const progressDiv = document.createElement("div")
        progressDiv.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border: 2px solid #007bff;
          border-radius: 10px;
          padding: 20px;
          z-index: 9999;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          min-width: 350px;
        `
        progressDiv.innerHTML = `
          <div style="text-align: center;">
            <h4>Processing Excel Export...</h4>
            <div id="progress-text">Processing rows 0 of ${totalRows.toLocaleString()}</div>
            <div style="width: 300px; height: 20px; background: #f0f0f0; border-radius: 10px; margin: 10px 0;">
              <div id="progress-bar" style="width: 0%; height: 100%; background: #007bff; border-radius: 10px; transition: width 0.3s;"></div>
            </div>
            <div id="progress-percentage" style="font-size: 12px; color: #666;">0%</div>
            <button id="cancel-export" style="margin-top: 10px; padding: 5px 15px; background: #dc3545; color: white; border: none; border-radius: 5px; cursor: pointer;">Cancel</button>
          </div>
        `
        document.body.appendChild(progressDiv)

        // Add cancel functionality
        let isCancelled = false
        document
          .getElementById("cancel-export")
          .addEventListener("click", () => {
            isCancelled = true
            document.body.removeChild(progressDiv)
            if (window.excelWorksheet) {
              delete window.excelWorksheet
            }
          })

        // Process data in chunks
        const processChunk = (startIndex) => {
          if (isCancelled) return

          const endIndex = Math.min(startIndex + CHUNK_SIZE, totalRows)
          const chunk = value.slice(startIndex, endIndex)

          // Update progress
          const progressText = document.getElementById("progress-text")
          const progressBar = document.getElementById("progress-bar")
          const progressPercentage = document.getElementById(
            "progress-percentage",
          )
          const progress = Math.round((endIndex / totalRows) * 100)

          if (progressText)
            progressText.textContent = `Processing rows ${endIndex.toLocaleString()} of ${totalRows.toLocaleString()}`
          if (progressBar) progressBar.style.width = `${progress}%`
          if (progressPercentage)
            progressPercentage.textContent = `${progress}%`

          try {
            // Process this chunk
            const chunkData = chunk.map((row) => {
              let rowObject = {}

              if (type === "Organization Reporting") {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = row[col.props.field]
                  })
                } else {
                  rowObject["organization_name"] = row["organization_name"]
                  if (subtype !== "Activity") {
                    rowObject["location_names"] = row["location_names"]
                    rowObject["program_identity_names"] =
                      row["program_identity_names"]
                    rowObject["assigned_staff_id"] = row["assigned_staff_id"]
                    rowObject["facilitating_staff_alias_id"] =
                      row["facilitating_staff_alias_id"]
                    rowObject["facilitating_staff_name_first"] =
                      row["facilitating_staff_name_first"]
                    rowObject["facilitating_staff_name_last"] =
                      row["facilitating_staff_name_last"]
                    rowObject["participant_membership_id"] =
                      row["participant_membership_id"]
                    rowObject["participant_alias"] = row["participant_alias"]
                    rowObject["participant_name_first"] =
                      row["participant_name_first"]
                    rowObject["participant_name_last"] =
                      row["participant_name_last"]
                    rowObject["participant_email_account"] =
                      row["participant_email_account"]
                    rowObject["participant_membership_status"] =
                      row["participant_membership_status"]
                  } else {
                    rowObject["facilitating_staff_alias_id"] =
                      row["facilitating_staff_alias_id"]
                    rowObject["facilitating_staff_name_first"] =
                      row["facilitating_staff_name_first"]
                    rowObject["facilitating_staff_name_last"] =
                      row["facilitating_staff_name_last"]
                  }

                  column.forEach((col) => {
                    rowObject[col] = row[col]
                    if (col === "supervision__staff_membership_id") {
                      rowObject["supervision__staff_name"] =
                        row["supervision__staff_name"]
                    }
                  })
                }
              } else if (type === "Custom Reporting") {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = row[col.props.field]
                  })
                } else {
                  custom_report_column.forEach((col) => {
                    rowObject[col] = row[col]
                  })
                }
              } else if (type === "Microservices Reporting") {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = row[col.props.field]
                  })
                } else {
                  microservices_report_column.forEach((col) => {
                    rowObject[col] = row[col]
                  })
                }
              } else if (type === "Participant Reporting") {
                if (dtreorder) {
                  dtreorder.columns.forEach((col) => {
                    rowObject[col.props.field] = getParticipantFieldValue(
                      row,
                      col.props.field,
                    )
                  })
                } else {
                  selectedParticipantReportColumns.forEach((col) => {
                    rowObject[col] = getParticipantFieldValue(row, col)
                  })
                }
              }

              return rowObject
            })

            // Add chunk to worksheet
            if (startIndex === 0) {
              // First chunk - create worksheet
              const worksheet = xlsx.utils.json_to_sheet(chunkData)
              window.excelWorksheet = worksheet
            } else {
              // Subsequent chunks - append to existing worksheet
              const range = xlsx.utils.decode_range(
                window.excelWorksheet["!ref"],
              )

              // Convert chunkData from array of objects to array of arrays for sheet_add_aoa
              const chunkDataAsArrays = chunkData.map((row) => {
                // Ensure all values are valid and handle null/undefined values
                return Object.values(row).map((value) => {
                  if (value === null || value === undefined) {
                    return ""
                  }
                  // Convert to string to avoid any potential issues with complex objects
                  return String(value)
                })
              })

              try {
                const newRange = xlsx.utils.decode_range(
                  xlsx.utils.sheet_add_aoa(
                    window.excelWorksheet,
                    chunkDataAsArrays,
                    {
                      origin: `A${range.e.r + 2}`,
                    },
                  )["!ref"],
                )
                window.excelWorksheet["!ref"] = xlsx.utils.encode_range({
                  s: { r: 0, c: 0 },
                  e: { r: newRange.e.r, c: newRange.e.c },
                })
              } catch (sheetError) {
                console.error("Error adding chunk to worksheet:", sheetError)
                console.error("chunkDataAsArrays:", chunkDataAsArrays)
                throw sheetError
              }
            }

            // Continue with next chunk or finish
            if (endIndex < totalRows) {
              // Chrome-specific: Add longer delay and force garbage collection hint
              const isChrome =
                /Chrome/.test(navigator.userAgent) &&
                /Google Inc/.test(navigator.vendor)
              const delay = isChrome ? 50 : 10 // Longer delay for Chrome

              setTimeout(() => {
                // Force garbage collection hint for Chrome
                if (isChrome && window.gc) {
                  try {
                    window.gc()
                  } catch (e) {
                    // Ignore if gc is not available
                  }
                }
                processChunk(endIndex)
              }, delay)
            } else {
              // Finish processing
              // Validate worksheet structure
              if (!window.excelWorksheet || !window.excelWorksheet["!ref"]) {
                throw new Error(
                  "Invalid worksheet structure - missing worksheet or range reference",
                )
              }

              // Ensure the worksheet has valid data
              const range = xlsx.utils.decode_range(
                window.excelWorksheet["!ref"],
              )
              if (range.e.r < 0 || range.e.c < 0) {
                throw new Error("Invalid worksheet range - negative dimensions")
              }

              const workbook = {
                Sheets: { data: window.excelWorksheet },
                SheetNames: ["data"],
              }

              try {
                // Chrome-specific optimization: Use setTimeout to allow garbage collection
                setTimeout(() => {
                  try {
                    const excelBuffer = xlsx.write(workbook, {
                      bookType: "xlsx",
                      type: "array",
                    })

                    // Clean up
                    delete window.excelWorksheet
                    document.body.removeChild(progressDiv)

                    // Save file
                    const fileName =
                      type === "Organization Reporting" ? template : subtype
                    saveAsExcelFile(excelBuffer, fileName)
                  } catch (writeError) {
                    console.error("Error writing Excel file:", writeError)
                    document.body.removeChild(progressDiv)
                    if (window.excelWorksheet) {
                      delete window.excelWorksheet
                    }
                    alert(
                      "Error creating Excel file. Please try again or contact support.",
                    )
                  }
                }, 0)
              } catch (writeError) {
                console.error("Error in setTimeout:", writeError)
                document.body.removeChild(progressDiv)
                if (window.excelWorksheet) {
                  delete window.excelWorksheet
                }
                alert(
                  "Error creating Excel file. Please try again or contact support.",
                )
              }
            }
          } catch (error) {
            console.error("Error processing Excel export:", error)
            document.body.removeChild(progressDiv)
            if (window.excelWorksheet) {
              delete window.excelWorksheet
            }
            alert(
              "Error processing Excel export. Please try again or contact support.",
            )
          }
        }

        // Start processing
        processChunk(0)
      } else {
        // For smaller datasets, use the original approach
        const data = value.map((row) => {
          let rowObject = {}

          if (type === "Organization Reporting") {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = row[col.props.field]
              })
            } else {
              rowObject["organization_name"] = row["organization_name"]
              if (subtype !== "Activity") {
                rowObject["location_names"] = row["location_names"]
                rowObject["program_identity_names"] =
                  row["program_identity_names"]
                rowObject["assigned_staff_id"] = row["assigned_staff_id"]
                rowObject["facilitating_staff_alias_id"] =
                  row["facilitating_staff_alias_id"]
                rowObject["facilitating_staff_name_first"] =
                  row["facilitating_staff_name_first"]
                rowObject["facilitating_staff_name_last"] =
                  row["facilitating_staff_name_last"]
                rowObject["participant_membership_id"] =
                  row["participant_membership_id"]
                rowObject["participant_alias"] = row["participant_alias"]
                rowObject["participant_name_first"] =
                  row["participant_name_first"]
                rowObject["participant_name_last"] =
                  row["participant_name_last"]
                rowObject["participant_email_account"] =
                  row["participant_email_account"]
                rowObject["participant_membership_status"] =
                  row["participant_membership_status"]
              } else {
                rowObject["facilitating_staff_alias_id"] =
                  row["facilitating_staff_alias_id"]
                rowObject["facilitating_staff_name_first"] =
                  row["facilitating_staff_name_first"]
                rowObject["facilitating_staff_name_last"] =
                  row["facilitating_staff_name_last"]
              }

              column.forEach((col) => {
                rowObject[col] = row[col]
                if (col === "supervision__staff_membership_id") {
                  rowObject["supervision__staff_name"] =
                    row["supervision__staff_name"]
                }
              })
            }
          } else if (type === "Custom Reporting") {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = row[col.props.field]
              })
            } else {
              custom_report_column.forEach((col) => {
                rowObject[col] = row[col]
              })
            }
          } else if (type === "Microservices Reporting") {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = row[col.props.field]
              })
            } else {
              microservices_report_column.forEach((col) => {
                rowObject[col] = row[col]
              })
            }
          } else if (type === "Participant Reporting") {
            if (dtreorder) {
              dtreorder.columns.forEach((col) => {
                rowObject[col.props.field] = getParticipantFieldValue(
                  row,
                  col.props.field,
                )
              })
            } else {
              selectedParticipantReportColumns.forEach((col) => {
                rowObject[col] = getParticipantFieldValue(row, col)
              })
            }
          }

          return rowObject
        })
        const worksheet = xlsx.utils.json_to_sheet(data)
        const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] }
        const excelBuffer = xlsx.write(workbook, {
          bookType: "xlsx",
          type: "array",
        })
        const fileName = type === "Organization Reporting" ? template : subtype
        saveAsExcelFile(excelBuffer, fileName)
      }
    })
  }

  /**
   * Header component containing export buttons for CSV and Excel
   * Provides data export functionality with visual feedback
   */
  const headers = (
    <div className={styles.download_buttons}>
      <Button
        type="button"
        label="CSV"
        icon="pi pi-file"
        onClick={() => exportCSV(false)}
        className="mr-2"
        data-pr-tooltip="CSV"
      />
      <Button
        type="button"
        label="Excel"
        icon="pi pi-file-excel"
        onClick={() => exportExcel()}
        className="mr-2 p-button-success"
        data-pr-tooltip="XLS"
      />
      {/* PDF export button currently disabled */}
      {/* <Button type="button" icon="pi pi-file-pdf" onClick={exportPdf} className="mr-2 p-button-warning" data-pr-tooltip="PDF" /> */}
    </div>
  )

  /**
   * Custom day content renderer for the date picker
   * Adds visual indicators for weekend days
   * @param {Date} day - The date to render
   * @returns {JSX.Element} Customized day content
   */
  const customDayContent = (day) => {
    if (isWeekend(day)) {
      return (
        <div>
          <div
            style={{
              height: "5px",
              width: "5px",
              borderRadius: "100%",
              background: "orange",
              position: "absolute",
              top: 2,
              right: 2,
            }}
          />
          <span>{format(day, "d")}</span>
        </div>
      )
    }
  }

  /**
   * Renders the static range label for date selection
   * @returns {JSX.Element} CustomStaticRangeLabelContent component
   */
  const renderStaticRangeLabel = () => (
    <CustomStaticRangeLabelContent text={"This is for last [-] days: "} />
  )

  /**
   * Custom component for displaying static range labels with live updates
   * Updates the display every second to show current date/time
   */
  class CustomStaticRangeLabelContent extends React.Component {
    constructor(props) {
      super(props)

      this.state = {
        currentDateString: Date(),
      }

      // Set up interval to update the display every second
      this.intervalId = setInterval(() => {
        this.setState({
          currentDateString: Date(),
        })
      }, 1000)
    }

    /**
     * Cleanup interval on component unmount
     */
    componentWillUnmount() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
    }

    /**
     * CustomStaticRangeLabelContent component render method
     * Displays the current date/time with the provided text
     * @returns {JSX.Element} Formatted date/time display
     */
    render() {
      const { currentDateString } = this.state
      const { text } = this.props

      return (
        <span>
          <i>{text}</i>
          <br />
          <span className={"random-date-string"}>
            <b>{currentDateString}</b>
          </span>
        </span>
      )
    }
  }

  /**
   * Template for rendering the organization selection section header
   * Provides a collapsible section with toggle functionality
   * @param {Object} options - Template options including collapsed state and click handlers
   * @returns {JSX.Element} Organization section header with toggle button
   */
  const organization_part_template = (options) => {
    const toggleIcon = options.collapsed
      ? "pi pi-chevron-circle-down"
      : "pi pi-chevron-circle-up"
    const className = `${options.className} justify-content-start`

    return (
      <div className={className}>
        <strong>Organization Selections</strong>
        <button className={styles.toggler} onClick={options.onTogglerClick}>
          <span className={toggleIcon}></span>
          <Ripple />
        </button>
      </div>
    )
  }

  /**
   * Template for rendering the report type selection section header
   * Provides a collapsible section with toggle functionality
   * @param {Object} options - Template options including collapsed state and click handlers
   * @returns {JSX.Element} Report section header with toggle button
   */
  const report_part_template = (options) => {
    const toggleIcon = options.collapsed
      ? "pi pi-chevron-circle-down"
      : "pi pi-chevron-circle-up"
    const className = `${options.className} justify-content-start`

    return (
      <div className={className}>
        <strong>Report Selections</strong>
        <button className={styles.toggler} onClick={options.onTogglerClick}>
          <span className={toggleIcon}></span>
          <Ripple />
        </button>
      </div>
    )
  }

  /**
   * Template for rendering the date selection section header
   * Provides a collapsible section with toggle functionality
   * @param {Object} options - Template options including collapsed state and click handlers
   * @returns {JSX.Element} Date section header with toggle button
   */
  const date_template = (options) => {
    const toggleIcon = options.collapsed
      ? "pi pi-chevron-circle-down"
      : "pi pi-chevron-circle-up"
    const className = `${options.className} justify-content-start`

    return (
      <div className={className}>
        <strong>Date Selections</strong>
        <button className={styles.toggler} onClick={options.onTogglerClick}>
          <span className={toggleIcon}></span>
          <Ripple />
        </button>
      </div>
    )
  }

  useEffect(() => {
    setLocation(null)
    setProgram(null)
    setStatus(null)
    setStaff(null)
    setFacilitatingStaff(null)
  }, [organization])

  useEffect(() => {
    if (
      format(date_range[0].startDate, "yyyy-MM-dd") <=
      format(subDays(date_range[0].endDate, 365), "yyyy-MM-dd")
    ) {
      setDateRange([
        {
          ...date_range[0],
          endDate: addDays(date_range[0].startDate, 365),
        },
      ])
    }
  }, [
    format(date_range[0].startDate, "yyyy-MM-dd") <=
      format(subDays(date_range[0].endDate, 365), "yyyy-MM-dd"),
  ])

  // useEffect(() => {
  //   if (block_options && template && subtype === "Activity") {
  //     setBlock(["activity"])
  //   } else if (block_options && template && subtype === "Data Form") {
  //     setBlock(["form"])
  //   } else if (block_options && template && subtype === "Session") {
  //     setBlock(["session"])
  //   } else {
  //     setBlock(null)
  //   }
  // }, [block_options && template])

  return (
    <div className="layout-with-sidebar">
      <SidebarDashboardSupervisor />
      <div className="my-dashboard-wrapper">
        <div>
          <div className={styles.title}>
            <h1>Organization Reports</h1>
            <button onMouseEnter={onHover} onMouseLeave={onLeave}>
              <span
                className="pi pi-question-circle"
                style={{ height: 30 }}
              ></span>
            </button>
          </div>
          <div className={styles.title}>
            <Messages style={{ width: 1020 }} ref={message} />
          </div>
          <div className={styles.box}>
            <Panel
              headerTemplate={organization_part_template}
              className={styles.mypanel}
              toggleable
            >
              <div className={styles.filter} style={{ height: 580 }}>
                <div>
                  <strong>Select organizations:</strong>
                  <br />
                  <MultiSelect
                    style={{ width: 450 }}
                    id="organization"
                    value={organization}
                    options={organization_options}
                    onChange={(e) => {
                      setOrganization(e.target.value)
                      setType(null)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    maxSelectedLabels={2}
                    showClear
                    filter
                    placeholder="Select Organizations:"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="500px"
                  />
                </div>
                <br />
                <div>
                  <strong>Select locations:</strong>
                  <br />
                  <MultiSelect
                    style={{ width: 450 }}
                    id="location"
                    value={location}
                    options={location_options}
                    onChange={(e) => {
                      setLocation(e.target.value)
                      setType(null)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    maxSelectedLabels={2}
                    showClear
                    filter
                    placeholder="Select Locations:"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="500px"
                  />
                </div>
                <br />
                <div>
                  <strong>Select programs:</strong>
                  <br />
                  <MultiSelect
                    style={{ width: 450 }}
                    id="program"
                    value={program}
                    options={program_options}
                    onChange={(e) => {
                      setProgram(e.target.value)
                      setType(null)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    maxSelectedLabels={2}
                    showClear
                    filter
                    placeholder="Select Programs:"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="500px"
                  />
                </div>
                <br />
                <div>
                  <strong>Select participant status:</strong>
                  <br />
                  <MultiSelect
                    style={{ width: 450 }}
                    id="status"
                    value={status}
                    options={status_options}
                    onChange={(e) => {
                      setStatus(e.target.value)
                      setType(null)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    maxSelectedLabels={2}
                    showClear
                    filter
                    placeholder="Select Status:"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="500px"
                  />
                </div>
                <br />
                <div>
                  <strong>Select assigned staff:</strong>
                  <br />
                  <MultiSelect
                    style={{ width: 450 }}
                    id="staff"
                    value={staff}
                    options={staff_options}
                    onChange={(e) => {
                      setStaff(e.target.value)
                      setType(null)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    maxSelectedLabels={2}
                    showClear
                    filter
                    placeholder="Select Assigned Staff:"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="500px"
                  />
                </div>
                <br />
                <div>
                  <strong>Select facilitating staff: </strong>
                  <br />
                  <MultiSelect
                    style={{ width: 450 }}
                    id="facilitating_staff"
                    value={facilitating_staff}
                    options={staff_options}
                    onChange={(e) => {
                      setFacilitatingStaff(e.target.value)
                      setType(null)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    maxSelectedLabels={2}
                    showClear
                    filter
                    placeholder="Select Facilitating Staff:"
                    optionLabel="label"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    scrollHeight="500px"
                  />
                </div>
              </div>
            </Panel>
            <Panel
              headerTemplate={report_part_template}
              className={styles.mypanel}
              toggleable
            >
              <div className={styles.filter} style={{ height: 580 }}>
                <div>
                  <strong>Select type of report:</strong>
                  <br />
                  <Dropdown
                    style={{ width: 450 }}
                    id="type"
                    value={type}
                    options={report_type}
                    onChange={(e) => {
                      setType(e.target.value)
                      setSubtype(null)
                      setTemplate(null)
                      setBlock(null)
                      setColumn(null)
                      setCustomReportColumn(null)
                      setMicroServicesReportColumn(null)
                      setParticipantReportColumn(null)
                    }}
                    showClear
                    filter
                    placeholder="Select Type of Report:"
                  />
                </div>
                <br />
                {type !== null && type !== undefined ? (
                  <div>
                    <strong>Select report:</strong>
                    <br />
                    <Dropdown
                      style={{ width: 450 }}
                      id="subtype"
                      value={subtype}
                      options={report_sub_type}
                      onChange={(e) => {
                        setSubtype(e.target.value)
                        setTemplate(null)
                        setBlock(null)
                        setColumn(null)
                        setCustomReportColumn(null)
                        setMicroServicesReportColumn(null)
                        setParticipantReportColumn(null)
                        setDtreorder(false)
                        setDtfilter(false)
                      }}
                      showClear
                      filter
                      placeholder="Select Report:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                    />
                  </div>
                ) : null}
                <br />
                {subtype !== null &&
                subtype !== undefined &&
                type === "Organization Reporting" ? (
                  <div>
                    <strong>Select template:</strong>
                    <br />
                    <Dropdown
                      style={{ width: 450 }}
                      id="template"
                      value={template}
                      options={template_options}
                      onChange={(e) => {
                        setTemplate(e.target.value)
                        setBlock(null)
                        setColumn(null)
                      }}
                      showClear
                      filter
                      placeholder="Select Template:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      scrollHeight="500px"
                    />
                  </div>
                ) : null}
                {subtype !== null &&
                subtype !== undefined &&
                type === "Custom Reporting" ? (
                  <div>
                    <strong>Select reporting fields:</strong>
                    <br />
                    <MultiSelect
                      style={{ width: 450 }}
                      id="custom_column"
                      value={custom_report_column}
                      options={custom_column_options}
                      onChange={(e) => {
                        setCustomReportColumn(e.target.value)
                        setDtreorder(false)
                        setDtfilter(false)
                      }}
                      maxSelectedLabels={2}
                      showClear
                      filter
                      placeholder="Select Reporting Fields:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      scrollHeight="500px"
                    />
                  </div>
                ) : null}
                {subtype !== null &&
                subtype !== undefined &&
                type === "Microservices Reporting" ? (
                  <div>
                    <strong>Select reporting fields:</strong>
                    <br />
                    <MultiSelect
                      style={{ width: 450 }}
                      id="microservices_column"
                      value={microservices_report_column}
                      options={microservices_column_options}
                      onChange={(e) => {
                        setMicroServicesReportColumn(e.target.value)
                        setDtreorder(false)
                        setDtfilter(false)
                      }}
                      maxSelectedLabels={2}
                      showClear
                      filter
                      placeholder="Select Reporting Fields:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      scrollHeight="500px"
                    />
                  </div>
                ) : null}
                {subtype !== null &&
                subtype !== undefined &&
                type === "Participant Reporting" ? (
                  <div>
                    <strong>Select reporting fields:</strong>
                    <br />
                    <MultiSelect
                      style={{ width: 450 }}
                      id="participant_column"
                      value={participant_report_column}
                      options={participant_column_options}
                      onChange={(e) => {
                        setParticipantReportColumn(e.target.value)
                        setDtreorder(false)
                        setDtfilter(false)
                      }}
                      maxSelectedLabels={2}
                      showClear
                      filter
                      placeholder="Select Reporting Fields:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      scrollHeight="500px"
                    />
                  </div>
                ) : null}
                <br />
                {template !== null &&
                template !== undefined &&
                subtype !== undefined ? (
                  <div>
                    <strong>Select blocks:</strong>
                    <br />
                    <MultiSelect
                      style={{ width: 450 }}
                      id="block"
                      value={block}
                      options={block_options}
                      onChange={(e) => {
                        setBlock(e.target.value)
                        setColumn(null)
                      }}
                      maxSelectedLabels={2}
                      showClear
                      filter
                      placeholder="Select Blocks:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      scrollHeight="500px"
                    />
                  </div>
                ) : null}
                <br />
                {block != null &&
                block != undefined &&
                template != undefined ? (
                  <div>
                    <strong>Select reporting fields:</strong>
                    <br />
                    <MultiSelect
                      style={{ width: 450 }}
                      id="column"
                      value={column}
                      options={column_options}
                      onChange={(e) => setColumn(e.target.value)}
                      maxSelectedLabels={2}
                      showClear
                      filter
                      placeholder="Select Reporting Fields:"
                      optionLabel="label"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      scrollHeight="500px"
                    />
                  </div>
                ) : null}
              </div>
            </Panel>
            <Panel
              className={styles.mypanel}
              headerTemplate={date_template}
              toggleable
            >
              <div className={styles.right}>
                <strong>Select reporting date range:</strong>
              </div>
              <div className={styles.left} style={{ height: 556 }}>
                <DateRangePicker
                  onChange={(item) => setDateRange([item.selection])}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  minDate={new Date("2019-12-01")}
                  maxDate={new Date()}
                  ranges={date_range}
                  direction="horizontal"
                  editableDateInputs={true}
                  hasCustomRendering={true}
                  dayContentRenderer={customDayContent}
                  dateDisplayFormat={"MM-dd-yyyy"}
                  renderStaticRangeLabel={renderStaticRangeLabel}
                  staticRanges={[
                    ...defaultStaticRanges,
                    {
                      label: "This Year",
                      range: () => ({
                        startDate: startOfYear(new Date()),
                        endDate: endOfDay(new Date()),
                      }),
                      isSelected(range) {
                        const definedRange = this.range()
                        return (
                          isSameDay(range.startDate, definedRange.startDate) &&
                          isSameDay(range.endDate, definedRange.endDate)
                        )
                      },
                    },
                    {
                      label: "Last Year",
                      range: () => ({
                        startDate: startOfYear(addYears(new Date(), -1)),
                        endDate: endOfYear(addYears(new Date(), -1)),
                      }),
                      isSelected(range) {
                        const definedRange = this.range()
                        return (
                          isSameDay(range.startDate, definedRange.startDate) &&
                          isSameDay(range.endDate, definedRange.endDate)
                        )
                      },
                    },
                    {
                      label: "Hoy",
                      hasCustomRendering: true,
                      range: () => ({
                        startDate: new Date(),
                        endDate: new Date(),
                      }),
                      isSelected() {
                        return true
                      },
                    },
                  ]}
                  inputRanges={[
                    {
                      ...defaultInputRanges[0],
                      label: "Days up to Today",
                    },
                  ]}
                />
              </div>
            </Panel>
          </div>
          <div className={styles.button}>
            <Button
              type="button"
              label="Generate Report"
              icon="pi pi-eye"
              loading={loading}
              className={styles.report_button}
              disabled={
                (custom_report_column === null &&
                  column === null &&
                  microservices_report_column === null &&
                  participant_report_column === null) |
                (organization === null) |
                (organization === undefined) |
                (clear === true)
              }
              onClick={() => {
                setLoading(true)
                setDtreorder(false)
                setDtfilter(false)
                if (type === "Organization Reporting") {
                  fetchorganizationreports({
                    variables: {
                      organization_id: organization_transform,
                      template_title: template_transform,
                      block_name: block_transform,
                      columns: column_transform,
                      report_type: subtype,
                      start_date: format(date_range[0].startDate, "yyyy-MM-dd"),
                      end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      location_id: location_transform_name,
                      program_id: program_transform_name,
                      status: status_transform,
                      staff: staff_transform,
                      facilitating_staff: facilitating_staff_transform,
                    },
                    onCompleted: (data) => {
                      setLoading(false)
                      setClear(true)
                    },
                  })
                } else if (type === "Custom Reporting") {
                  if (subtype === "ISR Individual Sessions") {
                    fetchisr({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                        facilitating_staff: facilitating_staff_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "ISR Group Sessions") {
                    fetchisrgroup({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                        facilitating_staff: facilitating_staff_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Opioid Stimulant Specific") {
                    fetchos({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Activity Session") {
                    fetchac({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Demographics") {
                    fetchdm({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Direct Service Hours") {
                    fetchds({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Engagements") {
                    fetcheg({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "GPRA") {
                    fetchgp({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (
                    subtype === "Individual List of Incoming Referrals"
                  ) {
                    fetchlr({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Lyft Drop-off Location") {
                    fetchld({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Lyft") {
                    fetchly({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Supervision") {
                    fetchsp({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "DOC Incoming Referrals") {
                    fetchre({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (
                    subtype === "PRIME Participant Characteristics Report"
                  ) {
                    fetchprimechracteristics({
                      variables: {
                        organization_id: organization_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (
                    subtype === "PRIME Participant Demographics Report"
                  ) {
                    fetchprimedemographics({
                      variables: {
                        organization_id: organization_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "PRIME Intakes Report") {
                    fetchprimeintakes({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "PRIME Check-ins Report") {
                    fetchprimecheckins({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "PRIME GPRA Report") {
                    fetchprimegpra({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Unity Recovery RSS Report") {
                    fetchunityrss({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Unity Recovery Employment Report") {
                    fetchunityemployment({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (
                    subtype === "Unity Recovery Harm Reduction Report"
                  ) {
                    fetchunityhr({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Unity Recovery PPR Report") {
                    fetchunityppr({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Sages Army RSS Report") {
                    fetchsagesrss({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Contact Log") {
                    fetchcl({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (
                    subtype === "New Hampshire Medicaid Billing Report"
                  ) {
                    fetchnhmedicaidbilling({
                      variables: {
                        organization_name: organization_transform_name_join,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  }
                } else if (type === "Microservices Reporting") {
                  if (subtype === "GPRA Report") {
                    fetchgpra({
                      variables: {
                        organization_name: organization_transform_name_join,
                        program_id: program_transform_name_gpra,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Kiosk Check-in Activity Report") {
                    fetchkioskactivity({
                      variables: {
                        organization_name: organization_transform_name_join,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Referrals Report") {
                    fetchreferrals({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                        facilitating_staff: facilitating_staff_transform,
                        status: status_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Assessments BARC-10 Monthly Report") {
                    fetchbarc10monthly({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Assessments BARC-10 Report") {
                    fetchbarc10individual({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                        status: status_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Telerecovery Sessions Report") {
                    fetchtelerecoverysessions({
                      variables: {
                        organization_name: organization_transform_name_join,
                        location_id: location_transform_name,
                        program_id: program_transform_name,
                        start_date: format(
                          date_range[0].startDate,
                          "yyyy-MM-dd",
                        ),
                        end_date: format(date_range[0].endDate, "yyyy-MM-dd"),
                        staff: staff_transform,
                        status: status_transform,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  }
                } else if (type === "Participant Reporting") {
                  if (subtype === "Participant Contact Information") {
                    fetchparticipantcontact({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Participant Demographics") {
                    fetchparticipantdemographics({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (subtype === "Participant Engaged Services") {
                    fetchparticipantengaged({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  } else if (
                    subtype === "Participant Recovery Characteristics"
                  ) {
                    fetchparticipantcharacteristics({
                      variables: {
                        organization_name: organization_transform_name_join,
                      },
                      onCompleted: (data) => {
                        setLoading(false)
                        setClear(true)
                      },
                    })
                  }
                }
              }}
            />
            <Button
              type="button"
              label="Clear Data"
              icon="pi pi-eye-slash"
              className={styles.report_button}
              disabled={clear === false}
              onClick={() => {
                setClear(false)
                setDtreorder(false)
                setDtfilter(false)
              }}
            />
          </div>
          <div className={styles.table}>
            {organization_reports_data_transform && column && clear && (
              <div>
                <Tooltip target=".export-buttons>button" position="bottom" />
                <DataTable
                  ref={dt}
                  paginator
                  rows={5}
                  selectionMode="multiple"
                  sortMode="multiple"
                  emptyMessage="No data available"
                  exportFilename={template}
                  value={organization_reports_data_transform}
                  header={headers}
                  dataKey="id"
                  responsiveLayout="scroll"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  showGridlines
                  resizableColumns
                  removableSort
                  columnResizeMode="fit"
                  reorderableColumns
                  onColReorder={(e) => setDtreorder(e)}
                  onValueChange={(e) => setDtfilter(e)}
                >
                  <Column
                    key="organization_name"
                    field="organization_name"
                    sortable
                    header="organization_name"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  {subtype !== "Activity" ? (
                    <Column
                      key="location_names"
                      field="location_names"
                      sortable
                      header="location_names"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="program_identity_names"
                      field="program_identity_names"
                      sortable
                      header="program_identity_names"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  <Column
                    key="assigned_staff_id"
                    field="assigned_staff_id"
                    sortable
                    header="assigned_staff_id"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  <Column
                    key="facilitating_staff_alias_id"
                    field="facilitating_staff_alias_id"
                    sortable
                    header="facilitating_staff_alias_id"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  <Column
                    key="facilitating_staff_name_first"
                    field="facilitating_staff_name_first"
                    sortable
                    header="facilitating_staff_name_first"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  <Column
                    key="facilitating_staff_name_last"
                    field="facilitating_staff_name_last"
                    sortable
                    header="facilitating_staff_name_last"
                    style={{ minWidth: "15rem" }}
                    filter
                    filterPlaceholder="Search by name"
                  />
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_membership_id"
                      field="participant_membership_id"
                      sortable
                      header="participant_membership_id"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_alias"
                      field="participant_alias"
                      sortable
                      header="participant_alias"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_name_first"
                      field="participant_name_first"
                      sortable
                      header="participant_name_first"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_name_last"
                      field="participant_name_last"
                      sortable
                      header="participant_name_last"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_email_account"
                      field="participant_email_account"
                      sortable
                      header="participant_email_account"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {subtype !== "Activity" ? (
                    <Column
                      key="participant_membership_status"
                      field="participant_membership_status"
                      sortable
                      header="participant_membership_status"
                      style={{ minWidth: "15rem" }}
                      filter
                      filterPlaceholder="Search by name"
                    />
                  ) : null}
                  {column.map((col) => {
                    const columnsToRender = [col]

                    if (col === "supervision__staff_membership_id") {
                      columnsToRender.push("supervision__staff_name")
                    }

                    return columnsToRender.map((field) => (
                      <Column
                        key={field}
                        field={field}
                        sortable
                        header={field}
                        style={{ minWidth: "15rem" }}
                        filter
                        filterPlaceholder="Search by name"
                      />
                    ))
                  })}
                </DataTable>
              </div>
            )}
            {subtype === "ISR Individual Sessions" &&
              isrdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={isrdata.ReportISR}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "ISR Group Sessions" &&
              isrgroupdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={isrgroupdata.ReportISRGroupSession}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "GPRA Report" &&
              gpradata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={gpradata.ReportGPRAReports}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Referrals Report" &&
              referralsdata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={referralsdata.ReportReferralsReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Kiosk Check-in Activity Report" &&
              kioskactivitydata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={kioskactivitydata.ReportKioskActivityReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Contact Information" &&
              participantcontactdata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantcontactdata.ReportParticipantContactReport
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Demographics" &&
              participantdemographicsdata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantdemographicsdata.ReportParticipantDemographicsReport
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Engaged Services" &&
              participantengageddata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantengageddata.ReportParticipantEngagedReport
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Participant Recovery Characteristics" &&
              participantcharacteristicsdata &&
              participant_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      participantcharacteristicsdata.ReportParticipantCharacteristicsReport
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {participant_column_data.ReportParticipantColumns.map(
                      (col) => {
                        const normalizedColumnName =
                          normalizeParticipantColumnName(col.column_name)

                        if (
                          selectedParticipantReportColumns.includes(
                            normalizedColumnName,
                          )
                        ) {
                          return (
                            <Column
                              key={normalizedColumnName}
                              field={normalizedColumnName}
                              sortable
                              header={normalizedColumnName}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                              body={(rowData) =>
                                getParticipantFieldValue(
                                  rowData,
                                  normalizedColumnName,
                                )
                              }
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Opioid Stimulant Specific" &&
              osdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={osdata.ReportIndianaOSReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Contact Log" &&
              cldata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={cldata.ReportIndianaContactLogReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "New Hampshire Medicaid Billing Report" &&
              nhmedicaidbillingdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={nhmedicaidbillingdata.ReportNhMedicaidBillingReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}

            {subtype === "Activity Session" &&
              acdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={acdata.ReportIndianaActivitySessionReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Demographics" &&
              dmdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={dmdata.ReportIndianaDemographicsReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Direct Service Hours" &&
              dsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={dsdata.ReportIndianaDirectServiceReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Engagements" &&
              egdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={egdata.ReportIndianaEngagementReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "GPRA" && gpdata && custom_report_column && clear && (
              <div>
                <Tooltip target=".export-buttons>button" position="bottom" />
                <DataTable
                  ref={dt}
                  paginator
                  rows={5}
                  selectionMode="multiple"
                  sortMode="multiple"
                  emptyMessage="No data available"
                  exportFilename={subtype}
                  value={gpdata.ReportIndianaGPRAReport}
                  header={headers}
                  dataKey="id"
                  responsiveLayout="scroll"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  showGridlines
                  resizableColumns
                  removableSort
                  columnResizeMode="fit"
                  reorderableColumns
                  onColReorder={(e) => setDtreorder(e)}
                  onValueChange={(e) => setDtfilter(e)}
                >
                  {custom_column_data.ReportCustomColumn.map((col) => {
                    if (
                      custom_report_column !== null &&
                      custom_report_column.includes(col.column_name)
                    ) {
                      return (
                        <Column
                          key={col.column_name}
                          field={col.column_name}
                          sortable
                          header={col.column_name}
                          style={{ minWidth: "15rem" }}
                          filter
                          filterPlaceholder="Search by name"
                        />
                      )
                    }
                  })}
                </DataTable>
              </div>
            )}
            {subtype === "Individual List of Incoming Referrals" &&
              lrdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={lrdata.ReportIndianaListofReferralsReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Lyft Drop-off Location" &&
              lddata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={lddata.ReportIndianaLyftDropoffReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Lyft" && lydata && custom_report_column && clear && (
              <div>
                <Tooltip target=".export-buttons>button" position="bottom" />
                <DataTable
                  ref={dt}
                  paginator
                  rows={5}
                  selectionMode="multiple"
                  sortMode="multiple"
                  emptyMessage="No data available"
                  exportFilename={subtype}
                  value={lydata.ReportIndianaLyftReport}
                  header={headers}
                  dataKey="id"
                  responsiveLayout="scroll"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  showGridlines
                  resizableColumns
                  removableSort
                  columnResizeMode="fit"
                  reorderableColumns
                  onColReorder={(e) => setDtreorder(e)}
                  onValueChange={(e) => setDtfilter(e)}
                >
                  {custom_column_data.ReportCustomColumn.map((col) => {
                    if (
                      custom_report_column !== null &&
                      custom_report_column.includes(col.column_name)
                    ) {
                      return (
                        <Column
                          key={col.column_name}
                          field={col.column_name}
                          sortable
                          header={col.column_name}
                          style={{ minWidth: "15rem" }}
                          filter
                          filterPlaceholder="Search by name"
                        />
                      )
                    }
                  })}
                </DataTable>
              </div>
            )}
            {subtype === "Supervision" &&
              spdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={spdata.ReportIndianaSupervisionReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "DOC Incoming Referrals" &&
              redata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={redata.ReportIndianaReferralsReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Participant Characteristics Report" &&
              primecharacteristicsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      primecharacteristicsdata.ReportPrimeCharacteristicsReport
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Participant Demographics Report" &&
              primedemographicsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primedemographicsdata.ReportPrimeDemographicsReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Intakes Report" &&
              primeintakesdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primeintakesdata.ReportPrimeIntakesReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME Check-ins Report" &&
              primecheckinsdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primecheckinsdata.ReportPrimeCheckInsReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "PRIME GPRA Report" &&
              primegpradata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={primegpradata.ReportPrimeGPRAReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Assessments BARC-10 Monthly Report" &&
              barc10monthlydata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={barc10monthlydata.ReportBarc10MonthlyReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Assessments BARC-10 Report" &&
              barc10individualdata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={barc10individualdata.ReportBarc10IndividualReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Telerecovery Sessions Report" &&
              telerecoverysessionsdata &&
              microservices_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={
                      telerecoverysessionsdata.ReportTelerecoverySessionsReport
                    }
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {microservices_column_data.ReportMicroservicesColumns.map(
                      (col) => {
                        if (
                          microservices_report_column !== null &&
                          microservices_report_column.includes(col.column_name)
                        ) {
                          return (
                            <Column
                              key={col.column_name}
                              field={col.column_name}
                              sortable
                              header={col.column_name}
                              style={{ minWidth: "15rem" }}
                              filter
                              filterPlaceholder="Search by name"
                            />
                          )
                        }
                      },
                    )}
                  </DataTable>
                </div>
              )}
            {subtype === "Unity Recovery RSS Report" &&
              unityrssdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={unityrssdata.ReportUnityRSSReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Unity Recovery Employment Report" &&
              unityemploymentdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={unityemploymentdata.ReportUnityEmploymentReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Unity Recovery Harm Reduction Report" &&
              unityhrdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={unityhrdata.ReportUnityHRReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Unity Recovery PPR Report" &&
              unitypprdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={unitypprdata.ReportUnityPPRReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
            {subtype === "Sages Army RSS Report" &&
              sagesrssdata &&
              custom_report_column &&
              clear && (
                <div>
                  <Tooltip target=".export-buttons>button" position="bottom" />
                  <DataTable
                    ref={dt}
                    paginator
                    rows={5}
                    selectionMode="multiple"
                    sortMode="multiple"
                    emptyMessage="No data available"
                    exportFilename={subtype}
                    value={sagesrssdata.ReportSagesRSSReport}
                    header={headers}
                    dataKey="id"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    showGridlines
                    resizableColumns
                    removableSort
                    columnResizeMode="fit"
                    reorderableColumns
                    onColReorder={(e) => setDtreorder(e)}
                    onValueChange={(e) => setDtfilter(e)}
                  >
                    {custom_column_data.ReportCustomColumn.map((col) => {
                      if (
                        custom_report_column !== null &&
                        custom_report_column.includes(col.column_name)
                      ) {
                        return (
                          <Column
                            key={col.column_name}
                            field={col.column_name}
                            sortable
                            header={col.column_name}
                            style={{ minWidth: "15rem" }}
                            filter
                            filterPlaceholder="Search by name"
                          />
                        )
                      }
                    })}
                  </DataTable>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}
